// src/calendario/PlantillaTurnoAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

export const PlantillaTurnoAdmin = () => {
  const toast = useToast();

  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [plantillaSel, setPlantillaSel] = useState(null);

  const [editNombre, setEditNombre] = useState("");
  const [editDescripcion, setEditDescripcion] = useState("");
  const [editColor, setEditColor] = useState("#85C1E9");
  const [editSegmentos, setEditSegmentos] = useState([{ hora_inicio: "", hora_fin: "" }]);

  const [saving, setSaving] = useState(false);

  // =========================
  // Carga inicial
  // =========================
  useEffect(() => {
    cargarPlantillas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarPlantillas = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${Global.url}turnos/plantillas`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        toast.error("No se pudieron cargar las plantillas.");
        setPlantillas([]);
        return;
      }

      const data = await res.json().catch(() => []);
      setPlantillas(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Error al cargar plantillas:", e);
      toast.error("Error de conexión al cargar plantillas.");
      setPlantillas([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // Helpers
  // =========================
  const parseSegmentos = (raw) => {
    try {
      if (Array.isArray(raw)) return raw;
      if (!raw) return [];
      return JSON.parse(raw);
    } catch {
      return [];
    }
  };

  const segmentosToText = (segs) => {
    const arr = parseSegmentos(segs);
    if (!arr.length) return "-";
    return arr
      .map((s) => `${(s?.hora_inicio || "").slice(0, 5)}-${(s?.hora_fin || "").slice(0, 5)}`.trim())
      .filter(Boolean)
      .join(" · ");
  };

  // =========================
  // Acciones
  // =========================
  const eliminarPlantilla = async (id) => {
    if (!window.confirm("¿Eliminar esta plantilla?")) return;

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}turnos/plantillas/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        toast.error(t || "No se pudo eliminar la plantilla.");
        return;
      }

      toast.success("Plantilla eliminada.");
      await cargarPlantillas();
    } catch (e) {
      console.error("Error al eliminar plantilla:", e);
      toast.error("Error de conexión al eliminar la plantilla.");
    } finally {
      setSaving(false);
    }
  };

  const abrirModalEdicion = (plantilla) => {
    const segs = parseSegmentos(plantilla?.segmentos);
    setPlantillaSel(plantilla);

    setEditNombre(plantilla?.nombre || "");
    setEditDescripcion(plantilla?.descripcion || "");
    setEditColor(plantilla?.color || "#85C1E9");
    setEditSegmentos(segs.length ? segs : [{ hora_inicio: "", hora_fin: "" }]);

    setModalOpen(true);
  };

  const cerrarModal = () => {
    if (saving) return;
    setModalOpen(false);
    setPlantillaSel(null);

    setEditNombre("");
    setEditDescripcion("");
    setEditColor("#85C1E9");
    setEditSegmentos([{ hora_inicio: "", hora_fin: "" }]);
  };

  const validarEdicion = () => {
    if (!editNombre.trim()) return "El nombre es obligatorio.";
    if (!editSegmentos.length) return "Añade al menos un segmento.";
    for (let i = 0; i < editSegmentos.length; i++) {
      const s = editSegmentos[i];
      if (!s.hora_inicio || !s.hora_fin) return `Completa las horas del segmento ${i + 1}.`;
      // ✅ NO validamos inicio < fin (turnos 24/7 pueden cruzar medianoche)
    }
    return "";
  };

  const guardarEdicion = async () => {
    if (!plantillaSel?.id) return;
    if (saving) return;

    const err = validarEdicion();
    if (err) {
      toast.warning(err);
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${Global.url}turnos/plantillas/${plantillaSel.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          nombre: editNombre.trim(),
          descripcion: (editDescripcion || "").trim(),
          color: editColor,
          segmentos: editSegmentos,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.message || "No se pudo actualizar la plantilla.");
        return;
      }

      toast.success("Plantilla actualizada.");
      cerrarModal();
      await cargarPlantillas();
    } catch (e) {
      console.error("Error al guardar edición:", e);
      toast.error("Error de conexión al actualizar la plantilla.");
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // Segmentos (edición)
  // =========================
  const setSegmento = (idx, campo, valor) => {
    setEditSegmentos((prev) => prev.map((s, i) => (i === idx ? { ...s, [campo]: valor } : s)));
  };

  const agregarSegmento = () => {
    setEditSegmentos((prev) => [...prev, { hora_inicio: "", hora_fin: "" }]);
  };

  const eliminarSegmento = (idx) => {
    setEditSegmentos((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  // =========================
  // DataGrid rows + columns
  // =========================
  const rows = useMemo(() => {
    return (plantillas || []).map((p) => ({
      ...p,
      id: p.id,
      color_txt: p.color || "#85C1E9",
      segmentos_txt: segmentosToText(p.segmentos),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantillas]);

  const columns = useMemo(
    () => [
      { field: "nombre", headerName: "Nombre", flex: 1, minWidth: 200 },
      { field: "descripcion", headerName: "Descripción", flex: 1.2, minWidth: 240 },
      {
        field: "color_txt",
        headerName: "Color",
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Box
            title={params.value}
            sx={{
              width: 22,
              height: 22,
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.18)",
              backgroundColor: params.value || "#eee",
            }}
          />
        ),
      },
      { field: "segmentos_txt", headerName: "Segmentos", flex: 1.2, minWidth: 260, sortable: false },
      {
        field: "acciones",
        headerName: "Acciones",
        sortable: false,
        minWidth: 260,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", height: "100%", width: "100%" }}>
            <Button size="small" startIcon={<EditIcon />} onClick={() => abrirModalEdicion(params.row)} disabled={modalOpen}>
              Editar
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => eliminarPlantilla(params.row.id)}
              disabled={modalOpen || saving}
            >
              Eliminar
            </Button>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalOpen, saving]
  );

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Plantillas de turno
        </Typography>

        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            loading={loading}
            pagination
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, color: "text.secondary" }}>
                  {loading ? "Cargando…" : "No hay plantillas registradas."}
                </Box>
              ),
            }}
          />
        </Paper>

        {/* =========================
            Dialog: Editar plantilla
           ========================= */}
        <Dialog open={modalOpen} onClose={cerrarModal} fullWidth maxWidth="sm">
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            Editar plantilla
            <IconButton aria-label="Cerrar" onClick={cerrarModal} disabled={saving}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Nombre"
                value={editNombre}
                onChange={(e) => setEditNombre(e.target.value)}
                required
              />

              <TextField
                label="Descripción"
                value={editDescripcion}
                onChange={(e) => setEditDescripcion(e.target.value)}
                placeholder="Opcional"
              />

              {/* Color */}
              <TextField
                label="Color"
                value={editColor}
                onChange={(e) => setEditColor(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box
                        component="input"
                        type="color"
                        value={editColor}
                        onChange={(e) => setEditColor(e.target.value)}
                        aria-label="Seleccionar color"
                        style={{
                          width: 44,
                          height: 28,
                          border: "none",
                          background: "transparent",
                          padding: 0,
                          cursor: "pointer",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Segmentos */}
              <Stack spacing={1}>
                <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 700 }}>Segmentos (horarios)</Typography>
                  <Button type="button" size="small" startIcon={<AddIcon />} onClick={agregarSegmento}>
                    Añadir segmento
                  </Button>
                </Stack>

                <Stack spacing={1}>
                  {editSegmentos.map((seg, i) => (
                    <Stack
                      key={i}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      sx={{ alignItems: { sm: "end" } }}
                    >
                      <TextField
                        label={`Inicio (segmento ${i + 1})`}
                        type="time"
                        value={seg.hora_inicio || ""}
                        onChange={(e) => setSegmento(i, "hora_inicio", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                      />

                      <TextField
                        label="Fin"
                        type="time"
                        value={seg.hora_fin || ""}
                        onChange={(e) => setSegmento(i, "hora_fin", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                      />

                      <Box sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}>
                        <IconButton
                          aria-label={`Eliminar segmento ${i + 1}`}
                          onClick={() => eliminarSegmento(i)}
                          disabled={editSegmentos.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={cerrarModal} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={guardarEdicion} disabled={saving} startIcon={<SaveIcon />}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
